import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const [hasProfile, setHasProfile] = useState(null); // null = unknown, true/false once known
  const [credits, setCredits] = useState(null); // {balance, low, threshold, unlimited}
  const [activeProject, setActiveProject] = useState(null);

  const refreshActiveProject = useCallback(async () => {
    try {
      const { data } = await api.get("/projects/active");
      setActiveProject(data.project || null);
      return data.project;
    } catch (e) {
      return null;
    }
  }, []);

  const refreshCredits = useCallback(async () => {
    try {
      const { data } = await api.get("/credits/me");
      setCredits(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      const projects = data.projects || [];
      setHasProfile(projects.length > 0);
      const active = projects.find((p) => p.is_active) || projects[0] || null;
      if (active) setActiveProject((prev) => prev || { id: active.id, name: active.name, goal: active.goal });
      return projects;
    } catch (e) {
      console.warn("Projects check failed:", e);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("h2i_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        await Promise.all([refreshProfile(), refreshCredits(), refreshActiveProject()]);
      })
      .catch(() => {
        localStorage.removeItem("h2i_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, [refreshProfile, refreshCredits]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("h2i_token", data.token);
    // login response may omit ui_mode; fetch the full profile so gating is correct.
    const me = await api.get("/auth/me").then((r) => r.data).catch(() => data.user);
    setUser(me);
    await Promise.all([refreshProfile(), refreshCredits(), refreshActiveProject()]);
    return me;
  };

  const register = async (name, email, password, inviteCode) => {
    const { data } = await api.post("/auth/register", { name, email, password, invite_code: inviteCode });
    localStorage.setItem("h2i_token", data.token);
    setUser({ ...data.user, ui_mode: data.user.ui_mode || "guided" });
    setHasProfile(false);
    await refreshCredits();
    return data.user;
  };

  const setUiMode = useCallback(async (mode) => {
    const { data } = await api.post("/me/mode", { mode });
    setUser((u) => (u ? { ...u, ui_mode: data.ui_mode } : u));
    return data.ui_mode;
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed (clearing session anyway):", e);
    }
    localStorage.removeItem("h2i_token");
    setUser(false);
    setHasProfile(null);
    setCredits(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, ready, login, register, logout, hasProfile, setHasProfile, refreshProfile,
               credits, refreshCredits, activeProject, setActiveProject, refreshActiveProject,
               uiMode: user?.ui_mode || "advanced", setUiMode }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
